import { RouteObject } from 'react-router-dom';

import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import {
  CreateEditCoverPhotos,
  CreateEditDevice,
  CreateEditEvent,
  CreateEdit as CreateEditOrganization,
  CreateEditTheme,
  CreateEditUser,
  CreateVideo,
  Dashboard,
  DeviceDetails,
  Devices,
  DigitalSignageDevice,
  DigitalSignagePlaylists,
  Events,
  EventTemplates,
  EventView,
  OrganizationAssets,
  OrganizationDetails,
  Organizations,
  PaymentPlans,
  PlayerReleases,
  ProfilePage,
  Themes,
  UploadContentCoverPhoto,
  UploadContentCreateCoverPhoto,
  UploadContentCreateTributeVideo,
  UploadContentLiveStream,
  UploadContentMusic,
  UploadContentRequests,
  UploadContentText,
  UploadContentTributeVideos,
  UserDetails,
  Users,
  VideoThemeView
} from '@/pages';
import { DigitalSignagePlaylistDetails } from '@/pages/DigitalSignage/Playlists/DigitalSignagePlaylistDetails';
import { CreateEditTextOverlay } from '@/pages/Event/CreateEditTextOverlay/CreateEditTextOverlay';
import { UploadContentPhotosVideos } from '@/pages/Event/UploadContent/PhotosVideos';
import { CreateEditEventTemplate } from '@/pages/EventTemplates/CreateEdit';
import { EventTemplateDetails } from '@/pages/EventTemplates/EventTemplateDetails';
import { CreateEditPaymentPlan } from '@/pages/PaymentPlans/CreateEditPaymentPlan';
import { PaymentPlanDetails } from '@/pages/PaymentPlans/PaymentPlanDetails';
import { CreateEditPlayerRelease } from '@/pages/PlayerReleases/CreateEditPlayerRelease';
import { PlayerReleaseDetails } from '@/pages/PlayerReleases/PlayerReleaseDetails';
import { Playlists } from '@/pages/Playlists';
import { PlaylistDetails } from '@/pages/Playlists/PlaylistDetails';
import { Settings } from '@/pages/Settings';
import { StockLibrary } from '@/pages/StockLibrary/StockLibrary';
import { PrivateRoute } from '@/types';

import { ProtectedRoute } from '../ProtectedRoute';

export const privateRoutes: PrivateRoute[] = [
  {
    path: PRIVATE_PAGE_ROUTES.USERS,
    element: <Users />,
    permissions: [PERMISSION_TYPE.VIEW_USERS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_USER,
    element: <CreateEditUser />,
    // CREATE_UPLOADER_USER is a broader subset used by Admin, Manager, Super Manager, and Creator.
    // CREATE_USER is available only to Admin, Manager, and Super Manager, excluding Creator.
    permissions: [/*PERMISSION_TYPE.CREATE_USER,*/ PERMISSION_TYPE.CREATE_UPLOADER_USER]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_USER,
    element: <CreateEditUser />,
    permissions: [PERMISSION_TYPE.UPDATE_USER]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_USER,
    element: <UserDetails />,
    permissions: [PERMISSION_TYPE.GET_USER]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_EVENT,
    element: <CreateEditEvent />,
    permissions: [PERMISSION_TYPE.CREATE_EVENT]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_EVENT,
    element: <CreateEditEvent />,
    permissions: [PERMISSION_TYPE.UPDATE_EVENT]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EVENTS,
    element: <Events />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_EVENT,
    element: <EventView />,
    permissions: [PERMISSION_TYPE.GET_EVENT, PERMISSION_TYPE.ORG_ACCESS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_VIDEO,
    element: <CreateVideo />,
    permissions: [PERMISSION_TYPE.CREATE_TRIBUTE_VIDEO, PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_TRIBUTE_VIDEO,
    element: <CreateVideo />,
    permissions: [PERMISSION_TYPE.CREATE_TRIBUTE_VIDEO, PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_COVER_PHOTOS,
    element: <CreateEditCoverPhotos />,
    permissions: [
      PERMISSION_TYPE.VIEW_EVENTS,
      PERMISSION_TYPE.CREATE_COVER_PHOTO,
      PERMISSION_TYPE.ORG_ACCESS
    ],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_TEXT_OVERLAY,
    element: <CreateEditTextOverlay />,
    permissions: [PERMISSION_TYPE.CREATE_TEXT_OVERLAY]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_TEXT_OVERLAY,
    element: <CreateEditTextOverlay />,
    permissions: [PERMISSION_TYPE.UPDATE_TEXT_OVERLAY]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_TEXT_OVERLAY_FOR_ORGANIZATION,
    element: <CreateEditTextOverlay />,
    permissions: [PERMISSION_TYPE.CREATE_TEXT_OVERLAY]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_TEXT_OVERLAY_FOR_ORGANIZATION,
    element: <CreateEditTextOverlay />,
    permissions: [PERMISSION_TYPE.UPDATE_TEXT_OVERLAY]
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_COVER_PHOTO_CONTENT,
    element: <UploadContentCreateCoverPhoto />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.CREATE_COVER_PHOTO],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_COVER_PHOTO,
    element: <CreateEditCoverPhotos />,
    permissions: [
      PERMISSION_TYPE.VIEW_EVENTS,
      PERMISSION_TYPE.UPDATE_COVER_PHOTO,
      PERMISSION_TYPE.ORG_ACCESS
    ],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_COVER_PHOTO_CONTENT,
    element: <UploadContentCreateCoverPhoto />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.UPDATE_COVER_PHOTO],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.COVER_PHOTOS,
    element: <UploadContentCoverPhoto />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.VIEW_COVER_PHOTOS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.COVER_PHOTO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_TEXT_CONTENT,
    element: <UploadContentText />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_REQUESTS,
    element: <UploadContentRequests />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_PHOTO_VIDEO,
    element: <UploadContentPhotosVideos />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_TRIBUTE_VIDEO,
    element: <UploadContentTributeVideos />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_MUSIC,
    element: <UploadContentMusic />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_CREATE_TRIBUTE_VIDEO,
    element: <UploadContentCreateTributeVideo />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_LIVE_STREAM,
    element: <UploadContentLiveStream />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.CREATE_STREAM_INVITATION],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.UPLOAD_CONTENT_VIEW_TRIBUTE_VIDEO,
    element: <UploadContentCreateTributeVideo />,
    permissions: [PERMISSION_TYPE.VIEW_EVENTS, PERMISSION_TYPE.EVENT_ACCESS],
    organizationPermissions: [
      ORGANIZATION_PERMISSION_TYPE.FAMILY_PORTAL,
      ORGANIZATION_PERMISSION_TYPE.TRIBUTE_VIDEO_CREATION,
      ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
    ],
    uploaderOnly: true
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_DEVICE,
    element: <CreateEditDevice />,
    permissions: [PERMISSION_TYPE.CREATE_DEVICE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_DEVICE,
    element: <CreateEditDevice />,
    permissions: [PERMISSION_TYPE.UPDATE_DEVICE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.DEVICES,
    element: <Devices />,
    permissions: [PERMISSION_TYPE.VIEW_DEVICES]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_DEVICE,
    element: <DeviceDetails />,
    permissions: [PERMISSION_TYPE.GET_DEVICE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_ORGANIZATION,
    element: <CreateEditOrganization />,
    permissions: [PERMISSION_TYPE.CREATE_ORGANIZATION]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_ORGANIZATION,
    element: <CreateEditOrganization />,
    permissions: [PERMISSION_TYPE.UPDATE_ORGANIZATION]
  },
  {
    path: PRIVATE_PAGE_ROUTES.ORGANIZATIONS,
    element: <Organizations />,
    permissions: [PERMISSION_TYPE.VIEW_ORGANIZATIONS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_ORGANIZATION,
    element: <OrganizationDetails />,
    permissions: [PERMISSION_TYPE.VIEW_ORGANIZATIONS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.ADD_DEVICE,
    element: <CreateEditDevice />,
    permissions: [PERMISSION_TYPE.CREATE_DEVICE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.PROFILE,
    element: <ProfilePage />
  },
  {
    path: PRIVATE_PAGE_ROUTES.THEMES,
    element: <Themes />,
    permissions: [PERMISSION_TYPE.VIEW_THEMES, PERMISSION_TYPE.CREATE_VIDEO_THEME]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_VIDEO_THEME,
    element: <VideoThemeView />,
    permissions: [PERMISSION_TYPE.GET_VIDEO_THEME, PERMISSION_TYPE.UPDATE_VIDEO_THEME]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_THEME,
    element: <CreateEditTheme />,
    permissions: [PERMISSION_TYPE.CREATE_VIDEO_THEME]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_THEME,
    element: <CreateEditTheme />,
    permissions: [PERMISSION_TYPE.UPDATE_VIDEO_THEME]
  },
  {
    path: PRIVATE_PAGE_ROUTES.STOCK_CLIPS,
    element: <StockLibrary />,
    permissions: [PERMISSION_TYPE.VIEW_STOCK_CLIPS, PERMISSION_TYPE.CREATE_STOCK_CLIP]
  },
  {
    path: PRIVATE_PAGE_ROUTES.PAYMENT_PLANS,
    element: <PaymentPlans />,
    permissions: [PERMISSION_TYPE.VIEW_PAYMENT_PLANS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_PAYMENT_PLAN,
    element: <PaymentPlanDetails />,
    permissions: [PERMISSION_TYPE.GET_PAYMENT_PLAN]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_PAYMENT_PLAN,
    element: <CreateEditPaymentPlan />,
    permissions: [PERMISSION_TYPE.CREATE_PAYMENT_PLAN]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_PAYMENT_PLAN,
    element: <CreateEditPaymentPlan />,
    permissions: [PERMISSION_TYPE.UPDATE_PAYMENT_PLAN]
  },
  {
    path: PRIVATE_PAGE_ROUTES.PLAYLISTS,
    element: <Playlists />,
    permissions: [PERMISSION_TYPE.VIEW_PLAYLISTS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_PLAYLISTS,
    element: <PlaylistDetails />,
    permissions: [PERMISSION_TYPE.UPDATE_PLAYLISTS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_SCREENS,
    element: <DigitalSignageDevice />,
    permissions: [PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_PLAYLISTS,
    element: <DigitalSignagePlaylists />,
    permissions: [PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_SINGLE_PLAYLIST,
    element: <DigitalSignagePlaylistDetails />,
    permissions: [PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.DIGITAL_SIGNAGE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.ORGANIZATION_ASSETS,
    element: <OrganizationAssets />,
    permissions: [PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.MEDIA_LIBRARY]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SETTINGS,
    element: <Settings />,
    permissions: [PERMISSION_TYPE.GLOBAL_ACCESS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.PLAYER_RELEASES,
    element: <PlayerReleases />,
    permissions: [PERMISSION_TYPE.GLOBAL_ACCESS]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_PLAYER_RELEASE,
    element: <CreateEditPlayerRelease />,
    permissions: [PERMISSION_TYPE.CREATE_PLAYER_RELEASE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_PLAYER_RELEASE,
    element: <CreateEditPlayerRelease />,
    permissions: [PERMISSION_TYPE.UPDATE_PLAYER_RELEASE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_PLAYER_RELEASE,
    element: <PlayerReleaseDetails />,
    permissions: [PERMISSION_TYPE.UPDATE_PLAYER_RELEASE]
  },
  {
    path: PRIVATE_PAGE_ROUTES.DASHBOARD,
    element: <Dashboard />,
    permissions: [PERMISSION_TYPE.VIEW_DASHBOARD]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CEREMONY_TEMPLATES,
    element: <EventTemplates />,
    permissions: [PERMISSION_TYPE.VIEW_EVENT_TEMPLATES],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP]
  },
  {
    path: PRIVATE_PAGE_ROUTES.CREATE_CEREMONY_TEMPLATE,
    element: <CreateEditEventTemplate />,
    permissions: [PERMISSION_TYPE.CREATE_EVENT_TEMPLATE],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP]
  },
  {
    path: PRIVATE_PAGE_ROUTES.EDIT_CEREMONY_TEMPLATE,
    element: <CreateEditEventTemplate />,
    permissions: [PERMISSION_TYPE.UPDATE_EVENT_TEMPLATE],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP]
  },
  {
    path: PRIVATE_PAGE_ROUTES.SINGLE_CEREMONY_TEMPLATE,
    element: <EventTemplateDetails />,
    permissions: [PERMISSION_TYPE.GET_EVENT_TEMPLATE, PERMISSION_TYPE.ORG_ACCESS],
    organizationPermissions: [ORGANIZATION_PERMISSION_TYPE.CEREMONY_SETUP]
  }
];

export const privateRouteObjects: RouteObject[] = privateRoutes.map((route) => ({
  path: route.path,
  element: <ProtectedRoute route={route} />
}));
